import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TextField,
  Stack,
} from "@mui/material";
import {
  attentionBillImport,
  billImportPurposes,
  billImports,
  getBankAccounts,
  unmappedPurposes,
} from "../API/nimble";
import { useLoaderData } from "react-router-dom";
import AddNewPurpose from "./AddNewPurpose";
import { enqueueSnackbar } from "notistack";
import { Loader } from "../Components/Components";
import { AccountTypesEnum } from "./ImportFilesDialog";

const AttentionNeededDialog = ({
  open,
  onClose,
  corporation,
  selectedRow,
  getUploadedGridData,
}) => {
  const { token } = useLoaderData();
  const [loading, setLoading] = useState(false);
  const [purposes, setpurposes] = useState([]);
  const [purposeList, setPurposeList] = useState([]);
  const [COAList, setCOAList] = useState([]);
  const [purposeNames, setPurposeNames] = useState([]);
  const [COA, setCOA] = useState([]);
  const [openAddPurposeDialog, setOpenAddPurposeDialog] = useState(false);
  const [manualChangeFlags, setManualChangeFlags] = useState([]);
  const [selectedAddRow, setSelectedAddRow] = useState(null);

  const columns2 = ["Division Code - Desc", "Purpose Name", "Chart of Account"];
  const [purposeToCOAMap, setPurposeToCOAMap] = useState({});
  const [originalCOA, setOriginalCOA] = useState([]);

  useEffect(() => {
    handleAttention();
  }, []);

  const handleAttention = async () => {
    setLoading(true);
    try {
      const corpId = corporation.ID;

      const unPurposes = await unmappedPurposes(token, selectedRow.id);
      const purposeHandle = await billImportPurposes(corpId, token);
      const coaList = await getBankAccounts(
        corporation.ID,
        "000000000000000000000000000000000000",
        token
      );

      const CoaAccounts = Array.isArray(coaList)
        ? coaList.filter((a) => {
            const accountTypeID = a?.AccountTypeID || a?.AccountTypeId || "";
            return (
              accountTypeID !== AccountTypesEnum.AccountsReceivable &&
              accountTypeID !== AccountTypesEnum.AccountsPayable
            );
          })
        : [];

      const preOptionsForPurpose = [
        { name: "-Select-" },
        { name: "-Add new-" },
      ];
      const preOptionsForCOA = [{ Name: "-Select-", ID: "select" }];

      const newPurposeList = [
        ...preOptionsForPurpose,
        ...purposeHandle.purposeResponses,
      ];

      const seenIDs = new Set();
      const uniqueCOAList = [];
      CoaAccounts.forEach((item) => {
        if (item.ID && !seenIDs.has(item.ID)) {
          seenIDs.add(item.ID);
          uniqueCOAList.push(item);
        }
      });
      const newCOAList = [...preOptionsForCOA, ...uniqueCOAList];

      const p2cMap = {};
      purposeHandle.purposeResponses.forEach((item) => {
        if (item.id) p2cMap[item.id] = item;
      });
      setPurposeToCOAMap(p2cMap);

      const updatedPurposeNames = purposeNames.length
        ? [...purposeNames]
        : new Array(unPurposes.unammpedPurpose.length).fill({
            name: "-Select-",
          });

      const updatedCOA = COA.length
        ? [...COA]
        : new Array(unPurposes.unammpedPurpose.length).fill({
            Name: "-Select-",
            ID: "select",
          });

      if (selectedAddRow !== null) {
        const oldIds = new Set(purposeList.map((p) => p.id));
        const addedPurpose = purposeHandle.purposeResponses.find(
          (p) => !oldIds.has(p.id)
        );
        if (addedPurpose) {
          updatedPurposeNames[selectedAddRow] = addedPurpose;
          const matchCOA = newCOAList.find(
            (c) => c.ID === addedPurpose.accountID
          );
          updatedCOA[selectedAddRow] = matchCOA || {
            Name: "-Select-",
            ID: "select",
          };
        }
        setSelectedAddRow(null);
      }

      setPurposeNames(updatedPurposeNames);
      setCOA(updatedCOA);
      setpurposes(unPurposes.unammpedPurpose);
      setPurposeList(newPurposeList);
      setCOAList(newCOAList);
      setManualChangeFlags(
        new Array(unPurposes.unammpedPurpose.length).fill(false)
      );
    } catch (error) {
      enqueueSnackbar("Error while Attention pop-up open", {
        variant: "error",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const preparePayload = () => {
    const purposeAccountMappings = [];
    const needToUpdatePurposeList = [];

    purposes.forEach((division, i) => {
      const p = purposeNames[i];
      const c = COA[i];

      if (!p?.id || !c?.ID) return;

      if (manualChangeFlags[i]) {
        needToUpdatePurposeList.push({
          purposeId: p.id,
          divisionCode:
            typeof division === "string"
              ? division
              : division?.divisionCode || "",
          accountId: c.ID,
          type: p.typeName,
        });
      } else {
        purposeAccountMappings.push({
          purposeId: p.id,
          divisionCode:
            typeof division === "string"
              ? division
              : division?.divisionCode || "",
          accountId: c.ID,
          type: p.typeName,
        });
      }
    });

    return { purposeAccountMappings, needToUpdatePurposeList };
  };

  function removeType(arr) {
    return arr.map(({ type, ...rest }) => rest);
  }

  const importBill = async () => {
    const uploadId = selectedRow?.id || selectedRow?.upload_id;
    if (!uploadId) {
      enqueueSnackbar("Missing upload id.", { variant: "error" });
      return;
    }

    let allMapped = true;

    purposes.forEach((_, i) => {
      const p = purposeNames[i];
      const c = COA[i];

      if (!p || !p.id || !c || !c.ID || c.ID === "select") {
        allMapped = false;
      }
    });

    if (!allMapped) {
      enqueueSnackbar(
        "All Purpose Names and COA must be mapped before posting the bill.",
        { variant: "warning" }
      );
      return;
    }

    setLoading(true);

    try {
      const payload = preparePayload();

      if (payload.needToUpdatePurposeList.length > 0) {
        const nonValidNewMap = payload.needToUpdatePurposeList.filter(
          (i) => i.type === "Non Validated"
        );
        payload.purposeAccountMappings.push(...nonValidNewMap);
      }
      const checkValidInNeedToUpdatePurposeList =
        payload.needToUpdatePurposeList.some(
          (item) => item.type === "Validated"
        );

      if (
        payload.needToUpdatePurposeList.length > 0 &&
        checkValidInNeedToUpdatePurposeList
      ) {
        const userConfirmed = window.confirm(
          "Do you want to update new account to purpose or limited to these entries?"
        );

        if (userConfirmed) {
          const nonValidNewMap = payload.needToUpdatePurposeList.filter(
            (i) => i.type === "Validated"
          );
          payload.purposeAccountMappings.push(...nonValidNewMap);
        } else {
          const validNewMap = payload.needToUpdatePurposeList.filter(
            (i) => i.type === "Validated"
          );
          payload.purposeAccountMappings.push(...validNewMap);

          payload.needToUpdatePurposeList = payload.needToUpdatePurposeList.map(
            (item) => {
              if (item.type === "Validated") {
                const updated = purposeList.find(
                  (p) => p.id === item.purposeId
                );
                if (updated) {
                  return {
                    ...item,
                    accountId: updated.accountID,
                    purposeId: updated.id,
                  };
                }
              }
              return item;
            }
          );
        }

        // if (userConfirmed) {
        //   const merged = payload.needToUpdatePurposeList.map((updatedItem) => {
        //     const idx = purposeNames.findIndex(
        //       (p) => p.id === updatedItem.purposeId
        //     );

        //     return {
        //       purposeId: updatedItem.purposeId,
        //       divisionCode: updatedItem.divisionCode,
        //       accountId: updatedItem.accountId,
        //     };
        //   });

        //   payload.needToUpdatePurposeList = payload.needToUpdatePurposeList.map(
        //     (item) => {
        //       const idx = purposeNames.findIndex(
        //         (p) => p.id === item.purposeId
        //       );
        //       const divisionObj = purposes[idx];
        //       const divisionCode =
        //         typeof divisionObj === "string"
        //           ? divisionObj
        //           : divisionObj?.divisionCode || "";

        //       return {
        //         purposeId: item.purposeId,

        //         accountId: originalCOA[idx],
        //         divisionCode: item.divisionCode,
        //       };
        //     }
        //   );
        //   payload.purposeAccountMappings.push(...merged);
        // }
      }

      payload.needToUpdatePurposeList.forEach((obj) => delete obj.type);
      payload.purposeAccountMappings.forEach((obj) => delete obj.type);

      // console.log("payload", payload);

      const billPost = await attentionBillImport(uploadId, payload, token)

      if (billPost.statusCode === 400) {
          enqueueSnackbar(`${billPost.status}`, { variant: "warning" });
          return;
      }

      if (billPost.statusCode === 200) {
          enqueueSnackbar(`${billPost.status}`, { variant: "success" });
          onClose();
          getUploadedGridData();
      }
    } catch (error) {
      enqueueSnackbar("Error while Bill import", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{ sx: { minHeight: "300px", minWidth: "1000px" } }}
      >
        <DialogTitle
          bgcolor={"#f4fee6"}
          display={"flex"}
          justifyContent={"flex-end"}
          gap={0.5}
        >
          <Button
            sx={{
              bgcolor: "white",
              border: "1px solid #ccc",
              color: "#63a103",
              textTransform: "none",
              minWidth: "22%",
              p: 0.2,
              borderRadius: 2,
              fontSize: 12,
            }}
            onClick={importBill}
          >
            Save & Post
          </Button>
          <Button
            variant="outlined"
            sx={{
              bgcolor: "white",
              border: "1px solid #ccc",
              color: "red",
              textTransform: "none",
              minWidth: "15%",
              p: 0.5,
              borderRadius: 2,
              fontSize: 12,
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
        </DialogTitle>
        <DialogContent>
          <Table
            sx={{
              borderCollapse: "collapse",
              width: "100%",
              tableLayout: "fixed",
              "& td, & th": {
                border: "1px solid #ddd",
                padding: "6px 10px",
                fontSize: 12,
              },
              "& th": {
                backgroundColor: "#e8f9f9",
                color: "#333",
                textAlign: "center",
                fontSize: 12,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columns2.map((col, index) => (
                  <TableCell key={index}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {purposes.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell>{row}</TableCell>

                  <TableCell>
                    <Autocomplete
                      size="small"
                      value={purposeNames[rowIndex] || null}
                      onChange={(e, newValue) => {
                        if (newValue?.name === "-Add new-") {
                          setSelectedAddRow(rowIndex);
                          setOpenAddPurposeDialog(true);
                          return;
                        }
                        const updatedPurposes = [...purposeNames];
                        updatedPurposes[rowIndex] = newValue;
                        setPurposeNames(updatedPurposes);

                        let matchedCOA = { Name: "-Select-", ID: "select" };

                        if (newValue?.accountID) {
                          matchedCOA = COAList.find(
                            (c) => c.ID === newValue.accountID
                          ) || {
                            Name: newValue.accountName || "-Select-",
                            ID: newValue.accountID,
                          };
                        }

                        const updatedCOA = [...COA];
                        updatedCOA[rowIndex] = matchedCOA;
                        setCOA(updatedCOA);
                        setOriginalCOA(
                          updatedCOA.map((coa) =>
                            coa?.ID === "select" ? null : coa?.ID
                          )
                        );

                        const flags = [...manualChangeFlags];
                        flags[rowIndex] = false;
                        setManualChangeFlags(flags);
                      }}
                      options={purposeList}
                      getOptionLabel={(option) => option.name || ""}
                      renderInput={(params) => <TextField {...params} />}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Stack
                            direction="row"
                            justifyContent={"space-between"}
                            width={"100%"}
                          >
                            <span
                              style={{ fontWeight: 400, whiteSpace: "nowrap" }}
                            >
                              {option.name}
                            </span>
                            <span style={{ fontSize: 12, opacity: 0.7 }}>
                              {option.typeName}
                            </span>
                          </Stack>
                        </li>
                      )}
                      slotProps={{
                        paper: {
                          sx: {
                            width: 350,
                          },
                        },
                        listbox: {
                          sx: {
                            width: 350,
                          },
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Autocomplete
                      size="small"
                      value={COA[rowIndex] || null}
                      onChange={(e, newValue) => {
                        const updated = [...COA];
                        if (!newValue || newValue.ID === "select") {
                          updated[rowIndex] = null;
                        } else {
                          updated[rowIndex] = newValue;
                        }

                        setCOA(updated);
                        const flags = [...manualChangeFlags];
                        flags[rowIndex] = newValue && newValue.ID !== "select";
                        setManualChangeFlags(flags);
                      }}
                      options={COAList}
                      getOptionLabel={(option) => option?.Name || ""}
                      // getOptionLabel={(option) =>
                      //     option ? `${option.Name} (${option.TypeName})` : ""
                      // }
                      renderInput={(params) => <TextField {...params} />}
                      isOptionEqualToValue={(option, value) =>
                        value === null
                          ? option.ID === "select"
                          : option.ID === value?.ID
                      }
                      clearOnEscape
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Stack
                            direction="row"
                            justifyContent={"space-between"}
                            width={"100%"}
                            spacing={2}
                          >
                            <span
                              style={{ fontWeight: 400, whiteSpace: "nowrap" }}
                            >
                              {option.Name}
                            </span>
                            <span style={{ fontSize: 12, opacity: 0.7 }}>
                              {option.AccountTypeName}
                            </span>
                          </Stack>
                        </li>
                      )}
                      slotProps={{
                        paper: {
                          sx: {
                            width: 450,
                          },
                        },
                        listbox: {
                          sx: {
                            width: 450,
                          },
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        {loading && <Loader loading={loading} />}
      </Dialog>

      {openAddPurposeDialog && (
        <AddNewPurpose
          open={openAddPurposeDialog}
          onClose={() => setOpenAddPurposeDialog(false)}
          corporation={corporation}
          savePurpose={handleAttention}
        />
      )}
    </>
  );
};

export default AttentionNeededDialog;
